import { type FC, useEffect, useMemo, useRef } from 'react';
import { DegreeCardDetailed } from '@highereducation/hen-ui';
import type { CardClickCorrelation, Result } from '../../types/result';
import { EventingLocation } from '../../types/enums';
import { uuid } from '../../shared/utils';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import { useTracking } from '../../providers/TrackingProvider';
import { useIntersect } from '../../hooks/useIntersect';
import { createProductViewedPayload } from '../../utils/createEventingPayloadsDE';
import { createResultsEventContext } from '../../utils/createResultsEventContext';
import tagularEvent from '../../utils/tagularEvent';
import { resolveActiveProductFlags } from '../ProductFlags/productFlagsResolve';
import { resolveRecommenderCtaState } from './recommenderCtaState';
import { shouldShowAccreditationBadge } from '../../utils/accreditationVisibility';
import { useAppViewConversion } from '../../hooks/useAppViewConversion';

export interface RecommenderCardProps {
  result: Result;
  position: number;
  isProgramSubmitted?: boolean;
  /**
   * When `true`, an already-submitted card delegates its click back to
   * `onProductClick` (the governed lead flow) instead of linking out to
   * the application URL or no-opping. The DSL result-page popup
   * (`DslResultsPagePopups`) sets this so a submitted Recommender card
   * reopens the governed exit-strategy view — matching `Spotlight`, which
   * always delegates. The legacy imperative recommender path leaves it
   * `false` to preserve its existing linkout / no-op behavior.
   */
  governSubmittedClick?: boolean;
  onProductClick: (correlation: CardClickCorrelation) => void;
}

/**
 * Hen-ui composed program card used inside the Recommender popup
 * (`ProgramSelect` step). Replaces the legacy `<Card isRecommender />`
 * compact recommender layout with the hen-ui `DegreeCardDetailed`
 * composite, surfacing the same school header, program info, product
 * flags as `tags`, snippet description, and Explore Program CTA the
 * legacy hand-rolled card exposed.
 *
 * Eventing parity with the legacy Card recommender path:
 * - `ProductLoaded` on mount.
 * - `ProductViewed` once the card scrolls into view.
 * - `ProductClicked` on Explore Program (then forwards to onProductClick).
 *
 * Location stays `EventingLocation.POPUP` so downstream funnels see
 * identical events to the previous implementation.
 */
const RecommenderCard: FC<RecommenderCardProps> = ({
  result,
  position,
  isProgramSubmitted = false,
  governSubmittedClick = false,
  onProductClick,
}) => {
  // DegreeCardDetailed forwards its ref to the underlying Card div, but
  // we wrap in our own div to (a) keep the IntersectionObserver target
  // independent of any future internal Card layout shifts and (b) keep
  // the flex sizing classes the parent grid relies on.
  const visibilityRef = useRef<HTMLDivElement>(null);
  const hasViewedRef = useRef(false);

  const {
    triggerProductLoadedEvent,
    triggerProductClickedEvent,
    eventContext,
  } = useFlowTrackingEvents();
  const { createAppViewConversion } = useAppViewConversion();

  // Present only when the recommender renders on the governed DSL result
  // page (inside `TrackingProvider`); `undefined` on the imperative
  // `ResultsPage` so the emitted product payloads stay byte-identical.
  const stepCorrelation = useTracking()?.stepCorrelation;
  const dslConfigId = stepCorrelation?.dslConfigId;
  const dslConfigVersion = stepCorrelation?.dslConfigVersion;

  // Per-instance correlation ids: useRef guarantees stability for this
  // card instance (unlike useMemo, which React may evict) so ProductLoaded
  // / ProductViewed / ProductClicked share one view id and the click →
  // conversion path ties to one click id. Each rendered instance mints its
  // own, so the same offering on another surface gets a distinct correlation.
  const instanceViewCorrelationId = useRef(uuid()).current;
  const instanceProductClickedCorrelationId = useRef(uuid()).current;

  const { setNodes, entries } = useIntersect();
  const isInView = entries[0]?.isIntersecting ?? false;

  const resultsEventContext = useMemo(
    () =>
      createResultsEventContext({
        eventContext,
        listId: result.listId,
        presets: {
          degree: result.degreeSlug || null,
          category: result.categorySlug || null,
          subject: result.subjectSlug || null,
        },
        location: EventingLocation.POPUP,
        productClickedCorrelationId: instanceProductClickedCorrelationId,
        viewCorrelationId: instanceViewCorrelationId,
        brand: result.schoolSlug,
        position,
        capId: result.capId,
        programId: result.programId,
        outboundUrl: result.applicationCtaURL,
        isProgramSubmitted,
        isUserRequested: result.isUserRequested,
        dslConfigId,
        dslConfigVersion,
      }),
    [
      eventContext,
      result,
      position,
      isProgramSubmitted,
      dslConfigId,
      dslConfigVersion,
      instanceViewCorrelationId,
      instanceProductClickedCorrelationId,
    ],
  );

  useEffect(() => {
    if (visibilityRef.current) {
      setNodes([visibilityRef.current]);
    }
  }, [setNodes]);

  useEffect(() => {
    if (hasViewedRef.current || !isInView) return;
    const payload = createProductViewedPayload(resultsEventContext as any);
    tagularEvent(payload.name, payload.payload as any);
    hasViewedRef.current = true;
    setNodes([]);
  }, [isInView, resultsEventContext, setNodes]);

  useEffect(() => {
    triggerProductLoadedEvent(resultsEventContext as any);
    // ProductLoaded fires once per card render; keep deps empty so we
    // don't double-fire when the eventContext memo identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Governed CTA state machine: the label and the click behavior are
  // resolved together (pure resolver) so they cannot drift.
  const ctaState = resolveRecommenderCtaState(result, {
    isProgramSubmitted,
    governSubmittedClick,
  });

  // Thin click dispatcher: always fire ProductClicked for tracking parity,
  // then route by the resolved intent. Missing-URL `appCta` degrades to a
  // no-op (fail-safe).
  const handleCtaClick = () => {
    triggerProductClickedEvent(resultsEventContext as any);
    switch (ctaState.intent) {
      case 'explore':
      case 'submitted':
        onProductClick({
          viewCorrelationId: instanceViewCorrelationId,
          productClickedCorrelationId: instanceProductClickedCorrelationId,
        });
        return;
      case 'appCta':
        if (ctaState.url) {
          // Routing into the external application view — create the CMS
          // "APP_VIEWED" conversion at this click (Supersonic parity), deduped
          // once per selection, before opening the application.
          createAppViewConversion(result);
          window.open(ctaState.url, '_blank', 'noopener,noreferrer');
        }
        return;
      case 'noop':
      default:
        return;
    }
  };

  const tags = deriveProgramTags(result);
  const schoolDetails =
    result.city && result.state ? `${result.city}, ${result.state}` : undefined;

  return (
    <div ref={visibilityRef} className='flex-1 min-w-0'>
      <DegreeCardDetailed
        schoolLogo={`https://simple-storage-server.highereducation.com/${result.schoolSlug}.png?w=130&h=80`}
        schoolName={result.schoolName}
        schoolDetails={schoolDetails}
        degreeTitle={result.programName}
        tags={tags}
        description={result.snippet ?? ''}
        ctaLabel={ctaState.label}
        onCtaClick={handleCtaClick}
        // compare={false}: comparing from inside a post-submission popup is
        // a confused UX — the user has already picked a school.
        compare={false}
        favorite={false}
        border='stroke'
      />
    </div>
  );
};

/**
 * Build the small `tags` badge list shown below the program title in
 * `DegreeCardDetailed`. Reuses `resolveActiveProductFlags` (the same
 * source `ProductFlags` reads from) so the labels stay in sync with
 * the rest of the app and the dictionary remains the single source of
 * truth. Includes the `notOnline` (On Campus) inverse flag and appends
 * the accreditator when present so the recommender card carries the
 * same trust signals the legacy `<ProductFlags simple />` pill row
 * exposed.
 */
function deriveProgramTags(result: Result): string[] {
  const flagSource = {
    isPrivate: result.isPrivate,
    isOnline: result.isOnline,
    isPublic: result.isPublic,
    forProfit: result.forProfit,
    notForProfit: result.notForProfit,
    notOnline: !result.isOnline,
  };
  const tags = resolveActiveProductFlags(flagSource).map(
    ({ flag }) => flag.value,
  );
  if (
    shouldShowAccreditationBadge(result.accreditator, result.institutionalStats)
  ) {
    tags.push('Accredited');
  }
  return tags;
}

export default RecommenderCard;
